import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiContext from "src/provider/API/call-service";
import AppInfoContext from "src/provider/state-manager/appInfoProvider";
import VisibilityContext from "src/provider/state-manager/visibilityProvider";
import {
  AppSpan,
  AppText,
  Button,
  Circle,
  CustomContainer,
  FlexColumn,
  FlexRow,
  GridContainer,
  Icon,
} from "src/style";
import { IoIosArrowBack } from "react-icons/io";
import ProfileBgImg from "src/assets/img/profile-bg-img.svg";
import utility, { moneyFormat } from "src/utils/utility";
import { useTranslation } from "react-i18next";
import { FiEdit, FiPlus } from "react-icons/fi";
import { ServerSideTable } from "src/component";
import { manageSurveyColumn, surveyDetailsColumn } from "src/utils/tableLayout";
import { NewSurveyQuestion } from "./popups/new-survey-question";
import { NewSurveyTrigger } from "../surveys/popups/new-survey-trigger";
import { ISurveyTriggers } from "src/model";

const SurveyDetails = () => {
  const navigate = useNavigate();
  const { API } = useContext(ApiContext);
  const { decisionBox } = useContext(VisibilityContext);
  const {
    info: {
      selectedSurveyTrigger,
      selectedInstitutionName,
      selectedInstitutionCode,
    },
  } = useContext(AppInfoContext);
  const { t } = useTranslation();
  const [surveyQuestions, setSurveyQuestions] = useState<any[]>([]);
  const [resetPagination, setResetPagination] = useState<boolean>(false);
  const [showNewQuestion, setShowNewQuestion] = useState(false);
  const [showEditSurveyTrigger, setShowEditSurveyTrigger] = useState(false);
  const [surveyTriggers, setSurveyTriggers] = useState<ISurveyTriggers[]>([]);
  const [initialSurveyQuestion, setInitialSurveyQuestion] =
    useState<Record<string, any>>();
  const [isEdit, setIsEdit] = useState(false);

  const surveyTriggerDetails = surveyTriggers?.find(
    (val: any) => val?.id === selectedSurveyTrigger?.id
  );

  async function fecthQuestions() {
    if (selectedInstitutionCode) {
      const response = await API.getSurveyQuestions(selectedInstitutionCode);
      if (response) setSurveyQuestions(response);
    }
  }

  async function fetchSurveyTrigger() {
    if (selectedInstitutionCode) {
      const response = await API.getSurveyTriggers(selectedInstitutionCode);
      if (response) setSurveyTriggers(response);
    }
  }

  useEffect(() => {
    fecthQuestions();
    fetchSurveyTrigger();
  }, [selectedInstitutionCode]);

  const handleEditQuestion = async (data: any) => {
    setIsEdit(true);
    setInitialSurveyQuestion(data);
    setShowNewQuestion(true);
  };

  return (
    <>
      <CustomContainer className="profile-container">
        <CustomContainer>
          <FlexRow
            justifyContent="flex-start"
            gap="1"
            width="auto"
            sizeUnit=""
            onClick={() => navigate(-1)}
          >
            <AppSpan textSize="2" cursor="pointer">
              <IoIosArrowBack />
            </AppSpan>
            <AppText
              textSize="3"
              fontWeight="600"
              color="#101828"
              cursor="pointer"
            >
              {t("surveyDetails.surveyDetails")}
            </AppText>
          </FlexRow>
          <FlexColumn
            className="survey-details-container"
            bgColor="#fff"
            height="10"
            alignItems="center"
            topMargin="3"
            radius="0.8"
            padding="2"
            justifyContent="space-between"
            leftPadding="4"
            rightPadding="4"
          >
            <FlexRow
              justifyContent="flex-start"
              alignItems="center"
              gap="15"
              wrap="wrap"
            >
              <CustomContainer width="auto" sizeUnit="">
                <AppText color="#58595B" fontWeight="700">
                  {t("surveyDetails.surveyName")}
                </AppText>
                <AppSpan color="#A098AE">
                  {surveyTriggerDetails?.triggerName}
                </AppSpan>
              </CustomContainer>
              <CustomContainer width="auto" sizeUnit="">
                <AppText color="#58595B" fontWeight="700">
                  {t("surveyDetails.institution")}
                </AppText>
                <AppSpan color="#A098AE">{selectedInstitutionName}</AppSpan>
              </CustomContainer>
              <CustomContainer width="auto" sizeUnit="">
                <AppText color="#58595B" fontWeight="700">
                  {t("surveyDetails.interval")}
                </AppText>
                <AppSpan color="#A098AE">
                  {surveyTriggerDetails?.surveyInterval}{" "}
                  {t("surveyDetails.days")}
                </AppSpan>
              </CustomContainer>
              <CustomContainer width="auto" sizeUnit="" alignItems="center">
                <AppText
                  textSize="1.4"
                  color="#0D968F"
                  fontWeight="700"
                  hoverColor="#667085"
                  cursor="pointer"
                  onClick={() => setShowEditSurveyTrigger(true)}
                >
                  <FiEdit size={20} color="##0A756F" />{" "}
                  {t("surveyDetails.editSurveyDetails")}
                </AppText>
                ,
              </CustomContainer>
            </FlexRow>
            <FlexRow
              justifyContent="flex-end"
              gap="1.5"
              wrap="wrap"
              topMargin="3"
            ></FlexRow>
          </FlexColumn>

          <CustomContainer>
            <FlexRow
              alignItems="center"
              justifyContent="space-between"
              topMargin="3"
            >
              <AppText
                textSize="2"
                fontWeight="900"
                color="#101828"
                topMargin="2"
              >
                {t("surveyDetails.questions")}
              </AppText>
              <Button
                width="22"
                titleSize="1.4"
                fontWeight="600"
                height="4"
                radius="0.8"
                hoverColor="#ffffffb9"
                borderColor="#D0D5DD"
                className="update-billers-btn"
                onClick={() => {
                  setIsEdit(false);
                  setShowNewQuestion(true);
                }}
              >
                <FlexRow alignItems="center" justifyContent="center">
                  <FiPlus size={20} color="#fff" />
                  {t("surveyDetails.addQuestion")}
                </FlexRow>
              </Button>
            </FlexRow>

            <FlexColumn
              className="survey-details-container"
              bgColor="#ffffff"
              minHeight="15"
              topMargin="2"
              radius="0.8"
              leftPadding="2"
              rightPadding="2"
              bottomPadding="2"
              justifyContent="flex-start"
            >
              <ServerSideTable
                tableData={surveyQuestions || []}
                tableColumns={surveyDetailsColumn(t, handleEditQuestion)}
                dataFetchMethod={fecthQuestions}
                totalRows={surveyQuestions?.length}
                tableTitle=""
                customStyle
                shouldResetPagination={resetPagination}
                selectInstitution={false}
                canFilter={false}
                canClear={false}
                downloadAction={false}
                showSurvey={false}
                showCreate={false}
                // setShowAddTriggger={() => setShowAddTrigger(true)}
              />
            </FlexColumn>
          </CustomContainer>
        </CustomContainer>
      </CustomContainer>

      {showNewQuestion && (
        <NewSurveyQuestion
          close={() => setShowNewQuestion(false)}
          refetchTriggers={fecthQuestions}
          surveyTriggerId={selectedSurveyTrigger?.id}
          ibInstitutionCode={selectedInstitutionCode}
          isEdit={isEdit}
          initialQuestionDetails={initialSurveyQuestion || {}}
        />
      )}

      {showEditSurveyTrigger && (
        <NewSurveyTrigger
          close={() => setShowEditSurveyTrigger(false)}
          ibInstitutionCode={selectedInstitutionCode}
          refetchTriggers={fetchSurveyTrigger}
          isEdit={true}
          initialSurveyTriggerDetails={surveyTriggerDetails}
        />
      )}
    </>
  );
};

export default utility.routeData(
  "survey-details",
  "Survey Details",
  <SurveyDetails />
);
